import { createContextFactory, useIsMobileShell } from '@mfw/components';
import React from 'react';

import { useAccountScopeReset } from './accountScope';

type NavigationContextType = {
  setMiniBarAvailable: React.Dispatch<React.SetStateAction<boolean>>;
  setNavigationOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isMiniBarAvailable?: boolean;
  isNavigationOpen: boolean;
  isMobile: boolean;
};

const NavigationContext = createContextFactory<NavigationContextType>({
  devToolsName: 'NavigationContext',
  initialValue: {} as NavigationContextType,
});

export interface NavigationProviderProps {
  children: React.ReactNode;
}
const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const isMobile = useIsMobileShell();
  const [isMiniBarAvailable, setMiniBarAvailable] = React.useState(false);
  const [isNavigationOpen, setNavigationOpen] = React.useState(!isMobile);

  /**
   * The shell belongs to the account that built it. This provider is remounted per route
   * branch, which used to be the only way to arrive as someone else — an identity can now
   * move with nothing unmounting (PR-2.1), and without this the next account inherits a
   * mini bar advertised for a page it cannot open and a drawer left wherever the previous
   * one put it. Back to the same values the two `useState` calls above start from, the
   * open state included: it is `!isMobile`, not a constant.
   */
  useAccountScopeReset(() => {
    setMiniBarAvailable(false);
    setNavigationOpen(!isMobile);
  });

  const providedValues = React.useMemo(() => {
    return {
      isMiniBarAvailable,
      isMobile,
      isNavigationOpen,
      setMiniBarAvailable,
      setNavigationOpen,
    };
  }, [
    setMiniBarAvailable,
    setNavigationOpen,
    isMobile,
    isMiniBarAvailable,
    isNavigationOpen,
  ]);
  return (
    <NavigationContext.Provider value={providedValues}>
      {children}
    </NavigationContext.Provider>
  );
};

export function useNavigationContext() {
  return NavigationContext.useContext();
}

export default NavigationProvider;
